import { useLayoutEffect, useState } from 'react';

/**
 * Maximum screen width (in pixels) to show the mobile warning.
 * Screens wider than this are allowed. Screens at or below this width
 * will show the warning and block the application.
 *
 * This threshold is set to 767px to match typical phone screen sizes,
 * but applies to any device with a small screen (including desktop
 * browsers with narrow windows).
 */
export const SMALL_SCREEN_MAX_WIDTH = 767;

/**
 * Detects if the current screen is too small to use the application.
 *
 * This checks only screen width, regardless of device type. Small browser
 * windows on desktop will also be blocked.
 *
 * @returns true if the screen width is at or below the threshold, false otherwise
 */
export function useIsSmallScreen(): boolean {
  const [isSmallScreen, setIsSmallScreen] = useState<boolean>(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    return window.innerWidth <= SMALL_SCREEN_MAX_WIDTH;
  });

  useLayoutEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth <= SMALL_SCREEN_MAX_WIDTH);
    };

    // Call on mount to sync state after SSR hydration
    // useLayoutEffect runs synchronously before paint, preventing flicker
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isSmallScreen;
}
